import { Box, Button, DialogTitle } from "@mui/material";
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import L from "leaflet";
import 'leaflet-providers';
import '@geoman-io/leaflet-geoman-free';
import '@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css';
import 'leaflet/dist/leaflet.css';
import { enqueueSnackbar } from "notistack";
import Iconify from "src/components/iconify/iconify";
import SimpleDialog from "src/components/simple-dialog";
import { ModelSavingData } from "src/hooks/use-model-saving-data";
import { EldLandmark, Shape } from "src/models";
import { formatAddress } from "src/utils/address-utils";

type EldLandmarkMapModalProps = {
  selectedEldLandmarkModel: ModelSavingData<EldLandmark>,
  isOpen: boolean,
  setIsOpen: Dispatch<SetStateAction<boolean>>,
  updateEldLandmark: VoidFunction
}

export function EldLandmarkMapModal(
  { selectedEldLandmarkModel, isOpen, setIsOpen, updateEldLandmark }: EldLandmarkMapModalProps
) {

  const layerGroupRef = React.useRef<L.LayerGroup<any> | null>(null);

  const getAllShapes = React.useCallback(() => {
    const allShapes = [] as Shape[];
    layerGroupRef.current?.eachLayer((layer) => {
      if (layer instanceof L.Circle) {
        allShapes.push({
          type: 'Circle',
          center: layer.getLatLng(),
          radius: layer.getRadius(),
        });
      } else if (layer instanceof L.Polygon) {
        allShapes.push({
          type: 'Polygon',
          coordinates: (layer.getLatLngs()[0] as L.LatLng[]).map(latLng => [latLng.lat, latLng.lng]),
        });
      }
    });
    return allShapes;
  }, [layerGroupRef]);

  // Function to save current shapes
  const saveShapes = () => {
    const shapesToSave = getAllShapes();
    selectedEldLandmarkModel.update((eldLandmark) => {
      eldLandmark.polygons = [];
      eldLandmark.circles = [];

      shapesToSave.forEach(shape => {
        if (shape.type === "Circle") {
          eldLandmark.circles.push({
            center: [shape.center.lat, shape.center.lng],
            radius: shape.radius
          })
        } else if (shape.type === "Polygon") {
          eldLandmark.polygons.push(
            shape.coordinates.map(c => [c[0], c[1]])
          )
        }
      })
    })
    selectedEldLandmarkModel.save().then(() => {
      enqueueSnackbar("Successfully saved the updates");
    }).catch(e => {
      console.error(e);
      enqueueSnackbar("Unexpected exception has occured. Please contact customer support.", {variant: "error"})
    })
  };


  return selectedEldLandmarkModel.model && <SimpleDialog
    fullScreen
    customBody
    open={isOpen}
    onCancel={() => {
      setIsOpen(false);
    }}
  >
    {selectedEldLandmarkModel.loading && <Box position="fixed" left="0px" zIndex="10001" top="0px" bottom="0px" right="0px" sx={{background: "#DDDDDD55"}} />}

    <Box display="flex" flexDirection="column" height="100%">
      <Box>
        <DialogTitle sx={{ p: (theme) => theme.spacing(2, 2, 1, 2), display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
          Update {selectedEldLandmarkModel.model?.name} shape

          <Box display="flex" flexDirection="row" gap={2}>
            <Button variant="soft" onClick={() => {
                setIsOpen(false);
              }} disabled={selectedEldLandmarkModel.loading}>
              Back
            </Button>
            <Button color="info" variant="contained" disabled={selectedEldLandmarkModel.loading} onClick={() => {
              saveShapes();
              // setIsOpen(false);
            }} startIcon={<Iconify icon="basil:save-solid" />}>
              Save
            </Button>
          </Box>
        </DialogTitle>
      </Box>

      <Map layerGroupRef={layerGroupRef} eldLandmarkModel={selectedEldLandmarkModel} initialAddress={formatAddress(selectedEldLandmarkModel.model.mainAddress)} />
    </Box>
  </SimpleDialog>
}

function Map({initialAddress, eldLandmarkModel, layerGroupRef}: {layerGroupRef: React.MutableRefObject<L.LayerGroup<any> | null>, initialAddress: string, eldLandmarkModel: ModelSavingData<EldLandmark>}) {

  const mapRef = React.useRef(null);
  const mapInstanceRef = React.useRef<L.Map | null>(null);

  const mapTypeSelectorState = useMapTypeSelector();

  // Function to load shapes onto the map
  const loadShapes = (shapesToLoad: Shape[]) => {
    // Clear existing shapes
    layerGroupRef.current?.clearLayers();

    // Add each shape to the map
    shapesToLoad.forEach(shape => {
      if (shape.type === 'Circle') {
        L.circle(shape.center, {
          radius: shape.radius
        }).addTo(layerGroupRef.current!);
      } else if (shape.type === 'Polygon') {
        L.polygon(shape.coordinates).addTo(layerGroupRef.current!);
      }
    });
  };

  // Function to load shapes from storage
  const loadSavedShapes = () => {
    if (eldLandmarkModel.model) {
      const {polygons, circles} = eldLandmarkModel.model;

      const savedShapes = [] as Shape[];

      polygons.forEach(polygon => {
        savedShapes.push({
          type: "Polygon",
          coordinates: polygon
        })
      });

      circles.forEach(circle => {
        savedShapes.push({
          type: "Circle",
          center: new L.LatLng(circle.center[0], circle.center[1]),
          radius: circle.radius,
        })
      })

      loadShapes(savedShapes);
    }
  };

  React.useEffect(() => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.eachLayer((layer) => {
        if (layer instanceof L.TileLayer) {
          mapInstanceRef.current!.removeLayer(layer);
        }
      });
      const currentZoom = mapInstanceRef.current.getZoom();
      const currentCenter = mapInstanceRef.current.getCenter();

      if (mapTypeSelectorState.defaultSelected) {
        L.tileLayer.provider('Stadia.AlidadeSmooth').addTo(mapInstanceRef.current)
      } else {
        L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.jpg', {
          minZoom: 0,
          maxZoom: 20,
        }).addTo(mapInstanceRef.current);
      }
      mapInstanceRef.current.setView(currentCenter, currentZoom);
    }
  }, [mapTypeSelectorState.defaultSelected]);

  React.useEffect(() => {
    // Initialize map if not already initialized
    if (!mapInstanceRef.current) {
      // Create map instance
      mapInstanceRef.current = L.map(mapRef.current!, { attributionControl: false }).setView([40, -96], 5);

      // Add OpenStreetMap tile layer
      if (mapTypeSelectorState.defaultSelected) {
        (mapInstanceRef.current as any).tileLayer = L.tileLayer.provider('Stadia.AlidadeSmooth').addTo(mapInstanceRef.current)
      } else {
        (mapInstanceRef.current as any).tileLayer = L.tileLayer('https://tiles.stadiamaps.com/tiles/alidade_satellite/{z}/{x}/{y}{r}.jpg', {
          minZoom: 0,
          maxZoom: 20,
        }).addTo(mapInstanceRef.current);
      }

  //     // L.tileLayer.provider('Stadia.AlidadeSatellite').addTo(_map)


      // Create a layer group for shapes
      layerGroupRef.current = L.layerGroup().addTo(mapInstanceRef.current);

      // Initialize Geoman controls
      mapInstanceRef.current.pm.addControls({
        position: 'topleft',
        drawCircle: true,
        drawPolygon: true,
        editMode: true,
        dragMode: true,
        cutPolygon: false,
        drawText: false,
        drawPolyline: false,
        drawMarker: false,
        drawCircleMarker: false,
        removalMode: true,
      });

      // Event handlers for shape creation
      mapInstanceRef.current.on('pm:create', (e) => {
        const {layer} = e;
        // Add new shape to the layer group
        layer.addTo(layerGroupRef.current!);
      });

      // Event handlers for shape editing
      // mapInstanceRef.current.on('pm:edit', (e) => {
      // });

      // // Event handlers for shape removal
      mapInstanceRef.current.on('pm:remove', (e) => {
        const {layer} = e;
        layerGroupRef.current!.removeLayer(layer);
      });

      loadSavedShapes();
    }

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addressRef = React.useRef(initialAddress);
  useEffect(() => {
    addressRef.current = initialAddress;
  }, [initialAddress]);

  const goToLocation = (lat: number, lon: number) => {
    if (mapInstanceRef.current) {
      mapInstanceRef.current.setView([lat, lon], 15);

      const marker = L.marker([lat, lon], {icon: L.divIcon({
        html: `
          <svg viewBox="0 0 24 24" width="36" height="36" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#FF0000"
              d="M12 0C7.802 0 4.4 3.402 4.4 7.6C4.4 11.798 12 24 12 24S19.6 11.798 19.6 7.6C19.6 3.402 16.198 0 12 0ZM12 10.5C10.397 10.5 9.1 9.203 9.1 7.6C9.1 5.997 10.397 4.7 12 4.7C13.603 4.7 14.9 5.997 14.9 7.6C14.9 9.203 13.603 10.5 12 10.5Z"
            />
          </svg>
        `,
        className: '',
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        popupAnchor: [0, -30],
      })})
          .addTo(mapInstanceRef.current)
          .bindPopup(addressRef.current, {className: "popup-no-tip", closeButton: false })
          .openPopup();

      setTimeout(() => mapInstanceRef.current && marker.removeFrom(mapInstanceRef.current), 4500);
    }
  }

  const goToLandmark = useCallback(() => {
    if (!eldLandmarkModel.model) {
      return;
    }

    const {latLoc, longLoc, circles, polygons} = eldLandmarkModel.model;

    if (latLoc !== 0 && longLoc !== 0) {
      goToLocation(latLoc, longLoc);
    } else if (circles.length > 0) {
      goToLocation(circles[0].center[0], circles[0].center[1]);
    } else if (polygons.length > 0) {
      goToLocation(polygons[0][0][0], polygons[0][0][1]);
    } else {
      enqueueSnackbar("Location could not be determined, please update the address or add geofensing data", {variant: "error"})
    }
  }, [eldLandmarkModel.model]);

  useEffect(() => {
    goToLandmark();
  }, [initialAddress, goToLandmark]);


  return <Box sx={{ flexGrow: 1, position: "relative" }}>
    <Box sx={{
      position: "absolute",
      top: "20px",
      right: "20px",
      zIndex: "1000",
      background: "white",
      padding: "10px",
      borderRadius: "4px",
      boxShadow: "0 0 10px rgba(0,0,0,0.2)",
    }}>
      {/* <Input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter address" /> */}
      {initialAddress}
      <Button variant="soft" size="small" sx={{pl: 1, pr: 1, minWidth: "initial", ml: 1, borderColor: "#999", color: "#222"}} onClick={() => goToLandmark()}><Iconify icon="mage:location-fill" /></Button>
      {/* <button type="button" onClick={() => saveShapes()}>Save Shapes</button>
      <button type="button" onClick={() => loadSavedShapes()}>Load</button> */}
    </Box>

    <MapTypeSelector mapTypeSelectorState={mapTypeSelectorState} />

    <div className="parent-container" ref={mapRef}>
      <div className="child-div" id="map" />
    </div>
  </Box>
}

type MapTypeSelectorState = {
  defaultSelected: boolean;
  setDefaultSelected: (val: boolean) => void;
}

export function useMapTypeSelector(): MapTypeSelectorState {
  const [defaultSelected, _setDefaultSelected] = useState(() => (localStorage.getItem("mapTypeDefault") || "true") === "true");
  return {
    defaultSelected, setDefaultSelected: (val: boolean) => {
      _setDefaultSelected(val);
      localStorage.setItem("mapTypeDefault", `${val}`);
    }
  }
}

export function MapTypeSelector({mapTypeSelectorState}: {mapTypeSelectorState: MapTypeSelectorState}) {
  return <Box sx={{
    position: "absolute",
    bottom: "20px",
    right: "20px",
    zIndex: "1000",
    background: "white",
    padding: "5px",
    borderRadius: "4px",
    boxShadow: "0 0 10px rgba(0,0,0,0.2)",
    display:"flex",
    flexDirection:"row",
    gap: "5px"
  }}>
    <Box sx={{cursor: "pointer"}} width="50px" fontSize="10px" textAlign="center" color={mapTypeSelectorState.defaultSelected ? "black" : "#888"} onClick={() => mapTypeSelectorState.setDefaultSelected(true)}>
      <Box borderRadius="4px" overflow="hidden">
        <img src="/assets/map/default.png" alt="" style={{border: mapTypeSelectorState.defaultSelected ? "3px solid #2196f3" : undefined}} />
      </Box>
      <Box paddingTop="5px">
        Default
      </Box>
    </Box>
    <Box sx={{cursor: "pointer"}} width="50px" fontSize="10px" textAlign="center" color={!mapTypeSelectorState.defaultSelected ? "black" : "#888"} onClick={() => mapTypeSelectorState.setDefaultSelected(false)}>
      <Box borderRadius="4px" overflow="hidden">
        <img src="/assets/map/satellite.png" alt="" style={{border: !mapTypeSelectorState.defaultSelected ? "3px solid #2196f3" : undefined}} />
      </Box>
      <Box paddingTop="5px">
        Satellite
      </Box>
    </Box>
  </Box>
}
